/**
 * AdvancedSearch — panneau de filtres avancés partagé
 * Utilisé dans PublicJurisprudence et PublicBrowse
 */

import { useTranslation } from 'react-i18next';

export interface AdvancedFilters {
  jurisdiction:  string;
  chamber:       string;
  domaine:       string;
  solution:      string;
  date_from:     string;
  date_to:       string;
  quality_min:   string;
  enriched_only: boolean;
}

export const EMPTY_ADV_FILTERS: AdvancedFilters = {
  jurisdiction:  '',
  chamber:       '',
  domaine:       '',
  solution:      '',
  date_from:     '',
  date_to:       '',
  quality_min:   '',
  enriched_only: false,
};

// Les `value` sont envoyées telles quelles à l'API — seuls les libellés (labelKey) sont traduits.
const JURIDICTIONS = [
  { value: '',                  labelKey: 'search.adv.all_jurisdictions' },
  { value: 'Cour de cassation', labelKey: 'search.adv.cour_cassation' },
  { value: "Cour d'appel",      labelKey: 'search.adv.cour_appel' },
];

// Domaines juridiques — valeurs réelles dans Qdrant
// Groupés par grande catégorie de la taxonomie
const DOMAINES = [
  { value: '',                    labelKey: 'search.adv.all_domains',          group: '' },
  // Droit privé
  { value: 'droit civil',         labelKey: 'search.adv.domain_civil',         group: 'prive' },
  { value: 'droit commercial',    labelKey: 'search.adv.domain_commercial',    group: 'prive' },
  { value: 'droit social',        labelKey: 'search.adv.domain_social',        group: 'prive' },
  { value: 'droit maritime',      labelKey: 'search.adv.domain_maritime',      group: 'prive' },
  // Droit public
  { value: 'droit administratif', labelKey: 'search.adv.domain_administratif', group: 'public' },
  { value: 'droit public',        labelKey: 'search.adv.domain_public',        group: 'public' },
  { value: 'droit électoral',     labelKey: 'search.adv.domain_electoral',     group: 'public' },
  // Droit répressif
  { value: 'droit pénal',         labelKey: 'search.adv.domain_penal',         group: 'repressif' },
  // Droit fiscal
  { value: 'droit fiscal',        labelKey: 'search.adv.domain_fiscal',        group: 'fiscalite' },
];

const DOMAINE_GROUPS = ['prive', 'public', 'repressif', 'fiscalite'];

const SOLUTIONS = [
  { value: '',           labelKey: 'search.adv.all_solutions' },
  { value: 'Cassation',  labelKey: 'search.solutions.cassation' },
  { value: 'Rejet',      labelKey: 'search.solutions.rejet' },
  { value: 'Autre',      labelKey: 'search.solutions.autre' },
];

const QUALITY_OPTIONS = [
  { value: '',    labelKey: 'search.adv.all_quality' },
  { value: '75',  labelKey: 'search.adv.score_75' },
  { value: '100', labelKey: 'search.adv.score_100' },
];

interface AdvancedSearchProps {
  filters:    AdvancedFilters;
  onChange:   (f: AdvancedFilters) => void;
  onApply:    () => void;
  onReset:    () => void;
  compact?:   boolean;
}

export const AdvancedSearchPanel = ({
  filters, onChange, onApply, onReset, compact = false,
}: AdvancedSearchProps) => {
  const { t } = useTranslation();
  const set = (key: keyof AdvancedFilters, value: string | boolean) =>
    onChange({ ...filters, [key]: value });

  const inputCls  = "w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  const labelCls  = "text-xs font-medium text-muted-foreground mb-1.5 block";

  return (
    <div className="mt-3 p-5 rounded-xl border bg-muted/30 space-y-4">
      <div className={`grid gap-4 ${compact ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>

        {/* Juridiction */}
        <div>
          <label className={labelCls}>{t('search.adv.jurisdiction')}</label>
          <select value={filters.jurisdiction} onChange={e => set('jurisdiction', e.target.value)} className={inputCls}>
            {JURIDICTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
          </select>
        </div>

        {/* Domaine juridique — dropdown structuré */}
        <div>
          <label className={labelCls}>{t('search.adv.domain')}</label>
          <select value={filters.domaine} onChange={e => set('domaine', e.target.value)} className={inputCls}>
            {DOMAINES.filter(o => !o.group).map(o => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
            {DOMAINE_GROUPS.map(group => {
              const opts = DOMAINES.filter(o => o.group === group);
              if (!opts.length) return null;
              return (
                <optgroup key={group} label={t(`search.adv.group_${group}`)}>
                  {opts.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                </optgroup>
              );
            })}
          </select>
        </div>

        {/* Solution */}
        <div>
          <label className={labelCls}>{t('search.adv.solution')}</label>
          <select value={filters.solution} onChange={e => set('solution', e.target.value)} className={inputCls}>
            {SOLUTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
          </select>
        </div>

        {/* Date de */}
        <div>
          <label className={labelCls}>{t('search.adv.date_from')}</label>
          <input type="date" value={filters.date_from} onChange={e => set('date_from', e.target.value)} className={inputCls} />
        </div>

        {/* Date à */}
        <div>
          <label className={labelCls}>{t('search.adv.date_to')}</label>
          <input type="date" value={filters.date_to} onChange={e => set('date_to', e.target.value)} className={inputCls} />
        </div>

        {/* Champs supplémentaires si non compact */}
        {!compact && (
          <>
            <div>
              <label className={labelCls}>{t('search.adv.chamber')}</label>
              <input type="text" value={filters.chamber} onChange={e => set('chamber', e.target.value)}
                placeholder={t('search.adv.chamber_placeholder')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('search.adv.quality_min')}</label>
              <select value={filters.quality_min} onChange={e => set('quality_min', e.target.value)} className={inputCls}>
                {QUALITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={filters.enriched_only}
                  onChange={e => set('enriched_only', e.target.checked)}
                  className="w-4 h-4 rounded border-input accent-primary" />
                <span className="text-sm">{t('search.adv.legal_graph_only')}</span>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between items-center pt-1 border-t">
        <button onClick={onReset} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          {t('search.adv.reset')}
        </button>
        <button onClick={onApply}
          className="text-sm px-5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-colors">
          {compact ? `${t('search.advanced_search')} →` : t('search.adv.apply')}
        </button>
      </div>
    </div>
  );
};

// ── Trigger bouton partagé ────────────────────────────────────────────────────

interface AdvancedSearchTriggerProps {
  open:             boolean;
  hasActiveFilters: boolean;
  onToggle:         () => void;
  onReset?:         () => void;
}

export const AdvancedSearchTrigger = ({
  hasActiveFilters, onToggle, onReset,
}: AdvancedSearchTriggerProps) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center justify-between mt-2 px-1">
    <button onClick={onToggle}
      className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round">
        <line x1="4" y1="6" x2="20" y2="6"/>
        <line x1="8" y1="12" x2="16" y2="12"/>
        <line x1="10" y1="18" x2="14" y2="18"/>
      </svg>
      {t('search.advanced_search')}
      {hasActiveFilters && (
        <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary inline-block" />
      )}
    </button>
    {hasActiveFilters && onReset && (
      <button onClick={onReset} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
        {t('search.clear_filters')}
      </button>
    )}
  </div>
  );
};

// ── Badge filtre actif ────────────────────────────────────────────────────────

interface ActiveFilterBadgeProps {
  label:    string;
  onRemove: () => void;
}

export const ActiveFilterBadge = ({ label, onRemove }: ActiveFilterBadgeProps) => (
  <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-full">
    {label}
    <button onClick={onRemove} className="hover:text-primary/60 transition-colors ml-0.5">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
);